


import {
  defineComponent,
  reactive,
  onMounted,
} from '@vue/composition-api';
import {
  convertNotificationToInputModel,
  createInitialInputModel,
  createInitialSearchParams,
  convertToDisplayData,
  NotificationInputModel,
} from '@/models/notification';
import { NotificationIndexParams } from '@/models/apis/notification/notificationRequest';
import { Notification } from '@/models/apis/notification/notificationResponse';
import Modal from '@/components/lib/Modal.vue';
import NotificationEditModal from '@/components/Admin/Notifications/NotificationEditModal/index.vue';
import NotificationTable from '@/components/Admin/Notifications/NotificationTable/index.vue';
import adminNotificationsApi from '@/apis/admin/admin_notifications';

export interface NotificationsState {
  notifications: Notification[];
  notification: NotificationInputModel;
  showEditModal: boolean;
  search: NotificationIndexParams;
  errorBody: string;
  showErrorModal: boolean;
}

export default defineComponent({
  components: {
    Modal,
    NotificationTable,
    NotificationEditModal,
  },

  setup() {
    const state = reactive<NotificationsState>({
      notifications: [],
      notification: createInitialInputModel(),
      showEditModal: false,
      search: createInitialSearchParams(),
      errorBody: '',
      showErrorModal: false,
    });

    const searchNotification = async(searchParams: NotificationIndexParams): Promise<void> => {
      try {
        await loadNotifications(searchParams);
      } catch (error) {
        onShowErrorModal('検索に失敗しました');
      }
    };
    const createNotification = (): void => {
      state.notification = createInitialInputModel();
      state.showEditModal = true;
    };
    const updateNotification = async(notification: Notification): Promise<void> => {
      const { data: notificationFiles } = await adminNotificationsApi.getFiles(notification.id);
      notification.files = notificationFiles;
      state.notification = convertNotificationToInputModel(notification);
      state.showEditModal = true;
    };
    const deleteNotification = async(id: number): Promise<void> => {
      try {
        await adminNotificationsApi.destroy(id);
        state.notifications = state.notifications.filter(n => n.id !== id);
      } catch (error) {
        onShowErrorModal('削除に失敗しました');
      }
    };
    const loadNotifications = async(searchParams: NotificationIndexParams): Promise<void> => {
      const processedParams = {
        ...searchParams,
        search_range_start: searchParams.search_range_start
          ? getStartOfDay(searchParams.search_range_start)
          : searchParams.search_range_start,
        search_range_end: searchParams.search_range_end
          ? getEndOfDay(searchParams.search_range_end)
          : searchParams.search_range_end,
      };

      const { data: notificationsData } = await adminNotificationsApi.index(processedParams);
      state.notifications = notificationsData.map((notification: Notification) =>
        convertToDisplayData(notification),
      );
    };
    const onCloseEditModal = (): void => {
      state.notification = createInitialInputModel();
      state.showEditModal = false;
    };
    const onEditSuccess = async(): Promise<void> => {
      state.notification = createInitialInputModel();
      state.showEditModal = false;
      await loadNotifications(state.search);
    };
    const onShowErrorModal = (message: string): void => {
      const trimmedMessage = message?.trim();
      if (trimmedMessage) {
        state.errorBody = trimmedMessage;
        state.showErrorModal = true;
      }
    };
    const onCloseErrorModal = (): void => {
      state.showErrorModal = false;
      state.errorBody = '';
    };
    const getStartOfDay = (date: Date): Date => {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    };
    const getEndOfDay = (date: Date): Date => {
      const newDate = new Date(date);
      newDate.setHours(23, 59, 59, 999);
      return newDate;
    };

    onMounted(async() => {
      await loadNotifications(state.search);
    });

    return {
      state,
      // methods
      searchNotification,
      createNotification,
      updateNotification,
      deleteNotification,
      onCloseEditModal,
      onEditSuccess,
      onShowErrorModal,
      onCloseErrorModal,
    };
  },
});
